/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2026 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2026 - Finnish Meteorological Institute (FMI)
 * Copyright 2026 - The Norwegian Meteorological Institute (MET Norway)
 * */

import {
  FilledInput,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import React from 'react';
import { convertNumericInputValue, getAllowedKeys } from '../../utils';

export interface NumberWithUnitOption {
  value: string;
  label: string;
}

export interface NumberWithUnitValue {
  value: number;
  unit: string;
}

export interface NumberWithUnitFieldProps {
  label: string;
  value: number;
  unit: string;
  unitOptions: NumberWithUnitOption[];
  onChange: (value: NumberWithUnitValue, isStep?: boolean) => void;
  onInputChange?: () => void;
  unitAriaLabel?: string;
  minValue?: number;
  maxDigits?: number;
  maxMagnitude?: number;
  error?: boolean;
  disabled?: boolean;
}

const arrowKeySteps: Record<string, number> = {
  ArrowUp: 1,
  ArrowDown: -1,
};

const integerKeys = getAllowedKeys('numeric');

const CARET_WIDTH = '24px';
const SIGN_WIDTH = '1ch';
// The unit adornment sits inside the filled input's right padding.
const FIELD_PADDING_RIGHT = '12px';

const transparentBackdrop = {
  slotProps: { backdrop: { sx: { backgroundColor: 'transparent' } } },
};

const countDigits = (text: string): number =>
  text.replace(/[^0-9]/g, '').length;

const styles = {
  field: {
    flexShrink: 0,
  },
  /* Filled inputs only offset start adornments for the label, so match that offset here. */
  unit: {
    flexShrink: 0,
    marginTop: '16px',
  },
};

/** The field underline is the focus marker, so it spans only the focused part instead of both. */
const partFocusUnderline =
  (unitWidth: string): SxProps<Theme> =>
  (theme) => ({
    '&:after': {
      borderColor: theme.palette.geowebColors.captions.captionInformation.rgba,
      right: `calc(${unitWidth} + ${FIELD_PADDING_RIGHT})`,
    },
    /* Mui-focused only follows the value input, so the unit has to claim the underline itself. */
    '&:has(.MuiSelect-select:focus):after': {
      left: 'auto',
      right: 0,
      transform: 'scaleX(1)',
      width: `calc(${unitWidth} + ${FIELD_PADDING_RIGHT})`,
    },
  });

const isClipboardShortcut = (event: React.KeyboardEvent): boolean => {
  const isMac = window.navigator.userAgent.includes(' Mac ');
  const hasModifier = isMac ? event.metaKey : event.ctrlKey;
  return hasModifier && ['c', 'v', 'x'].includes(event.key.toLowerCase());
};

export const NumberWithUnitField: React.FC<NumberWithUnitFieldProps> = ({
  label,
  value,
  unit,
  unitOptions,
  onChange,
  onInputChange,
  unitAriaLabel,
  minValue,
  maxDigits = 5,
  maxMagnitude,
  error = false,
  disabled = false,
}) => {
  const inputId = React.useId();
  const [typedValue, setTypedValue] = React.useState(String(value));

  React.useEffect(() => {
    setTypedValue(String(value));
  }, [value]);

  const clampValue = (newValue: number): number => {
    if (!Number.isFinite(newValue)) {
      return minValue ?? 0;
    }
    const truncated = Math.trunc(newValue);
    return minValue === undefined ? truncated : Math.max(minValue, truncated);
  };

  const commitTypedValue = (newValue: string, isStep = false): void => {
    const allowedDigits =
      maxMagnitude === undefined
        ? maxDigits
        : Math.min(maxDigits, String(maxMagnitude).length);
    const parsed = convertNumericInputValue(newValue, 'numeric');
    if (
      countDigits(newValue) > allowedDigits ||
      (parsed !== null &&
        maxMagnitude !== undefined &&
        Math.abs(parsed) > maxMagnitude)
    ) {
      return;
    }
    setTypedValue(newValue);
    onInputChange?.();
    if (parsed === null) {
      return;
    }
    if (isStep) {
      onChange({ value: clampValue(parsed), unit }, true);
      return;
    }
    onChange({ value: clampValue(parsed), unit });
  };

  /* Typed text can clamp onto the value already stored, which leaves no prop change to resync from. */
  const showStoredValue = (): void => {
    setTypedValue(String(value));
  };

  const stepValue = (step: number): void => {
    const parsed = convertNumericInputValue(typedValue, 'numeric');
    commitTypedValue(String(clampValue((parsed ?? value) + step)), true);
  };

  const handleKeyDown = (event: React.KeyboardEvent): void => {
    const step = arrowKeySteps[event.key];
    if (step !== undefined) {
      event.preventDefault();
      event.stopPropagation();
      stepValue(step);
      return;
    }

    if (isClipboardShortcut(event)) {
      return;
    }

    const isRepeatedMinus =
      event.key === '-' &&
      (event.target as HTMLInputElement).value.includes('-');

    if (!integerKeys.includes(event.key) || isRepeatedMinus) {
      event.preventDefault();
    }
  };

  const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>): void => {
    const input = event.target as HTMLInputElement;
    const pasted = event.clipboardData.getData('text/plain');
    const before = input.value.slice(0, input.selectionStart!);
    const after = input.value.slice(input.selectionEnd!);

    if (!/^-?[0-9]+$/.exec(before + pasted + after)) {
      event.preventDefault();
    }
  };

  const longestUnitLabel = unitOptions.reduce(
    (longest, option) => Math.max(longest, option.label.length),
    0,
  );

  const inputWidth = `calc(${maxDigits}ch + ${SIGN_WIDTH})`;
  /* Fixed so a longer unit grows the value box leftwards instead of widening the field. */
  const unitWidth = `calc(${longestUnitLabel}ch + ${CARET_WIDTH})`;

  return (
    <FormControl
      disabled={disabled}
      error={error}
      sx={styles.field}
      variant="filled"
    >
      <InputLabel htmlFor={inputId} shrink variant="filled">
        {label}
      </InputLabel>
      <FilledInput
        endAdornment={
          <FormControl sx={styles.unit} variant="standard">
            <Select
              MenuProps={transparentBackdrop}
              /* Select escalates its own min-width rule by repeating its class, so only an inline style wins. */
              SelectDisplayProps={{
                style: { textAlign: 'right', width: unitWidth },
              }}
              disableUnderline
              disabled={disabled}
              inputProps={{ 'aria-label': unitAriaLabel ?? label }}
              onChange={(event): void =>
                onChange({ value, unit: event.target.value })
              }
              value={unit}
              variant="standard"
            >
              {unitOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        }
        id={inputId}
        inputProps={{ inputMode: 'numeric', style: { width: inputWidth } }}
        onBlur={showStoredValue}
        onChange={(event): void => commitTypedValue(event.target.value)}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        sx={partFocusUnderline(unitWidth)}
        value={typedValue}
      />
    </FormControl>
  );
};
